// Supply Map block for the Article DPP panel (IT-3102).
// Renders the full traceability hierarchy exposed by the DPP endpoint (IT-3101):
//   - Article level: warehouse, article_finishing, manufacturing
//   - Material level (badge "material"): material_finishing, material_processing
//   - Composition level (badge "composition"): raw_material_processing, raw_material_extraction
// Each stage is an array of { supplier_name, origin }; empty arrays render "—".

import React from "react";
import { Typography } from "@material-ui/core";

const round2 = (n) => Math.round(n * 100) / 100;

const labelStyle = {
  textTransform: "uppercase",
  letterSpacing: 0.5,
  fontWeight: 600,
};

const badgeStyle = {
  display: "inline-block",
  marginLeft: 8,
  padding: "1px 8px",
  borderRadius: 10,
  fontSize: 11,
  lineHeight: "16px",
  backgroundColor: "#eeeeee",
  color: "#616161",
  verticalAlign: "middle",
};

const nestedStyle = {
  borderLeft: "2px solid #e0e0e0",
  paddingLeft: 16,
  marginTop: 12,
};

// One stage: uppercase grey label + one "Supplier · Origin" line per entry, "—" if empty.
const Stage = ({ label, entries }) => {
  const list = Array.isArray(entries) ? entries : [];
  return (
    <div style={{ marginBottom: 8 }}>
      <Typography variant="caption" color="textSecondary" component="div" style={labelStyle}>
        {label}
      </Typography>
      {list.length === 0 ? (
        <Typography variant="body2" color="textSecondary">
          —
        </Typography>
      ) : (
        list.map((entry, i) => (
          <Typography variant="body2" key={i}>
            {[entry.supplier_name, entry.origin].filter(Boolean).join(" · ") || "—"}
          </Typography>
        ))
      )}
    </div>
  );
};

const GroupHeader = ({ title, badge }) => (
  <Typography variant="body2" component="div" style={{ fontWeight: 600, marginBottom: 8 }}>
    {title}
    <span style={badgeStyle}>{badge}</span>
  </Typography>
);

const Composition = ({ composition }) => {
  const pct =
    composition.percentage === null || composition.percentage === undefined
      ? null
      : `${round2(composition.percentage)}%`;
  const title = [composition.fiber, pct].filter(Boolean).join(" ");
  return (
    <div style={nestedStyle}>
      <GroupHeader title={title || "—"} badge="composition" />
      <Stage label="Raw Material Processing" entries={composition.raw_material_processing} />
      <Stage label="Raw Material Extraction" entries={composition.raw_material_extraction} />
    </div>
  );
};

const Material = ({ material }) => {
  const compositions = Array.isArray(material.compositions) ? material.compositions : [];
  return (
    <div style={nestedStyle}>
      <GroupHeader title={material.material_name || "—"} badge="material" />
      <Stage label="Material Finishing" entries={material.material_finishing} />
      <Stage label="Material Processing" entries={material.material_processing} />
      {compositions.map((composition, i) => (
        <Composition key={`${composition.fiber || "fiber"}-${i}`} composition={composition} />
      ))}
    </div>
  );
};

// Renderer registered in sections.js. supply_map hangs off the response root
// (sibling of dpp_compliance), not inside it. Missing → null ("Not available").
const supplyMap = (_dpp, _article, response) => {
  const map = response?.supply_map;
  if (!map) return null;
  const materials = Array.isArray(map.materials) ? map.materials : [];
  return (
    <div>
      <Stage label="Warehouse" entries={map.warehouse} />
      <Stage label="Article Finishing" entries={map.article_finishing} />
      <Stage label="Manufacturing" entries={map.manufacturing} />
      {materials.map((material, i) => (
        <Material key={`${material.material_name || "material"}-${i}`} material={material} />
      ))}
    </div>
  );
};

export default supplyMap;
